import { useEffect, useState } from "react";
import { addSports, deleteSport, getSport, uploadImage } from "../service/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { RefreshCw, Trash2, AlertCircle } from "lucide-react";

const Sport = () => {
    const [name, setName] = useState<any>('');
    const [image, setImage] = useState<any>('');
    const [errors, setErrors] = useState<any>({});
    const [sports, setSports] = useState([]);
    const [loading, setLoading] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [deletedId, setDeletedId] = useState<any>();

    useEffect(() => {
        getAllSports();
    }, []);

    const getAllSports = async () => {
        setLoading(true);
        try {
            let res = await getSport();
            setSports(res.data.data);
        } finally {
            setLoading(false);
        }
    }

    const handleInputChange = (e: any) => {
        setName(e.target.value);
    };

    const handleImageChange = async (e: any) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file, file.name);
        try {
            const response = await uploadImage(formData);
            setImage(response.data.files[0]);
        } catch (error) {

            toast.error("Image upload failed");
        }
    };

    const handleSubmit = async () => {
        const newErrors: { name?: string; image?: string } = {};
        if (!name) newErrors.name = "Name is required";
        if (!image || image.length === 0) newErrors.image = "At least one image is required";
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        setErrors({});
        try {
            const body = { name, image };
            await addSports(body);
            toast.success("Sport added successfully");
            setName('');
            setImage('');
            getAllSports();
        } catch (err) {
            toast.error("Failed to add sport");
        }
    };

    const handleDelete = (data: any) => {
        setDeleteModal(true);
        setDeletedId(data.id);
    };

    const deleteSports = async () => {
        try {
            await deleteSport({ id: deletedId });
            toast.success("Sport deleted");
            setDeleteModal(false);
            getAllSports();
        } catch (err) {
            toast.error("Failed to delete sport");
        }
    };

    return (
        <div className="w-full max-w-6xl mx-auto pb-8 space-y-6">
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-foreground">Sports</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Sports appear in partner onboarding and turf configuration.
                    </p>
                </div>
                <Button variant="outline" size="sm" onClick={() => getAllSports()}>
                    <RefreshCw className="h-4 w-4" />
                </Button>
            </div>

            {/* Add form */}
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-base">Add sport</CardTitle>
                    <p className="text-sm text-muted-foreground">Upload an icon or photo for the sport.</p>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <Label htmlFor="sport-name">Name</Label>
                            <Input
                                id="sport-name"
                                type="text"
                                placeholder="e.g. Cricket"
                                value={name}
                                onChange={handleInputChange}
                            />
                            {errors?.name && <p className="text-destructive text-xs">{errors?.name}</p>}
                        </div>
                        <div className="space-y-1.5">
                            <Label>Image</Label>
                            <input
                                type="file"
                                onChange={handleImageChange}
                                className="block w-full text-sm text-muted-foreground file:mr-4 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-primary-foreground hover:file:bg-primary/90"
                            />
                            {errors?.image && <p className="text-destructive text-xs">{errors?.image}</p>}
                        </div>
                    </div>
                    <div className="pt-2 border-t">
                        <Button size="sm" onClick={handleSubmit}>Add sport</Button>
                    </div>
                </CardContent>
            </Card>

            {/* Table card */}
            <Card className="overflow-hidden">
                <div className="px-6 py-4 border-b">
                    <h2 className="text-base font-semibold">All sports</h2>
                </div>
                {loading ? (
                    <div className="flex min-h-[200px] items-center justify-center">
                        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-12">#</TableHead>
                                    <TableHead>Name</TableHead>
                                    <TableHead>Image</TableHead>
                                    <TableHead className="text-right w-24">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {sports.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} className="text-center text-muted-foreground py-14">
                                            No sports yet. Add one using the form above.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    sports.map((item: any, index: number) => (
                                        <TableRow key={item.id}>
                                            <TableCell className="text-muted-foreground">{index + 1}</TableCell>
                                            <TableCell className="font-medium">{item.name}</TableCell>
                                            <TableCell>
                                                <img
                                                    src={item.image}
                                                    className="h-14 w-14 rounded-lg object-cover border"
                                                    alt=""
                                                    onError={(e) => { e.currentTarget.src = "/wood-blog-placeholder.jpg"; }}
                                                />
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 text-destructive hover:text-destructive"
                                                    onClick={() => handleDelete(item)}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </Card>

            {/* Delete confirmation dialog */}
            <Dialog open={deleteModal} onOpenChange={setDeleteModal}>
                <DialogContent className="max-w-sm">
                    <DialogHeader className="items-center text-center">
                        <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-2" />
                        <DialogTitle>Delete this sport?</DialogTitle>
                        <DialogDescription>This action cannot be undone.</DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="flex-row justify-center gap-2 sm:justify-center">
                        <Button variant="outline" onClick={() => setDeleteModal(false)}>Cancel</Button>
                        <Button variant="destructive" onClick={deleteSports}>Delete</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default Sport;
